import { supabase } from '../lib/supabase';

/**
 * Pitch Training Service
 * Manages melody MIDI tracks and pitch training sessions
 */

// MIDI Track Management
export const pitchTrainingService = {
  // Upload and parse MIDI file - stores MIDI data in jsonb format
  async uploadMidiTrack(file, metadata) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('User not authenticated');

      // Save track metadata to database (no storage bucket needed - MIDI data stored in jsonb)
      const { data, error } = await supabase?.from('melody_midi_tracks')?.insert({
          created_by: user?.id,
          song_id: metadata?.songId || null,
          track_name: metadata?.trackName || file?.name?.replace(/\.[^/.]+$/, ''),
          track_index: metadata?.trackIndex || 0,
          midi_data: metadata?.midiData, // JSONB field containing parsed MIDI notes
          note_count: metadata?.noteCount || 0,
          lowest_note: metadata?.lowestNote || 0,
          highest_note: metadata?.highestNote || 127
        })?.select()?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error uploading MIDI track:', error);
      return { data: null, error: error?.message };
    }
  },

  // Get all MIDI tracks for a specific song
  async getTracksBySongId(songId) {
    try {
      const { data, error } = await supabase?.from('melody_midi_tracks')?.select('*')?.eq('song_id', songId)?.order('track_index', { ascending: true });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error fetching MIDI tracks:', error);
      return { data: null, error: error?.message };
    }
  },

  // Get all tracks (admin view)
  async getAllTracks() {
    try {
      const { data, error } = await supabase?.from('melody_midi_tracks')?.select(`
          *,
          songs (
            id,
            title
          )
        `)?.order('created_at', { ascending: false });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error fetching all MIDI tracks:', error);
      return { data: null, error: error?.message };
    }
  },

  // Get single track by ID
  async getTrackById(trackId) {
    try {
      const { data, error } = await supabase?.from('melody_midi_tracks')?.select(`
          *,
          songs (
            id,
            title
          )
        `)?.eq('id', trackId)?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error fetching track:', error);
      return { data: null, error: error?.message };
    }
  },

  // Update track metadata
  async updateTrack(trackId, updates) {
    try {
      const { data, error } = await supabase?.from('melody_midi_tracks')?.update(updates)?.eq('id', trackId)?.select()?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error updating track:', error);
      return { data: null, error: error?.message };
    }
  },

  // Delete track
  async deleteTrack(trackId) {
    try {
      const { error } = await supabase?.from('melody_midi_tracks')?.delete()?.eq('id', trackId);

      if (error) throw error;
      return { error: null };
    } catch (error) {
      console.error('Error deleting track:', error);
      return { error: error?.message };
    }
  },

  // Session Management
  async createSession(sessionData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('User not authenticated');

      const { data, error } = await supabase?.from('pitch_training_sessions')?.insert({
          user_id: user?.id,
          song_id: sessionData?.songId,
          melody_track_id: sessionData?.melodyTrackId,
          training_mode: sessionData?.trainingMode || 'legit_classical',
          pitch_tolerance_cents: sessionData?.pitchToleranceCents || 50,
          timing_window_ms: sessionData?.timingWindowMs || 500,
          vibrato_smoothing: sessionData?.vibratoSmoothing || 0.5,
          confidence_threshold: sessionData?.confidenceThreshold || 0.8,
          duration_seconds: sessionData?.durationSeconds,
          pitch_accuracy_percent: sessionData?.pitchAccuracyPercent,
          mean_cents_error: sessionData?.meanCentsError,
          note_hit_rate: sessionData?.noteHitRate,
          longest_streak: sessionData?.longestStreak,
          letter_grade: sessionData?.letterGrade,
          frame_results: sessionData?.frameResults || [], // Array of per-note results
          session_status: sessionData?.sessionStatus || 'completed',
          completed_at: sessionData?.sessionStatus === 'completed' ? new Date()?.toISOString() : null
        })?.select()?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error creating session:', error);
      return { data: null, error: error?.message };
    }
  },

  // Get user's session history
  async getUserSessions(limit = 20) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('User not authenticated');

      const { data, error } = await supabase?.from('pitch_training_sessions')?.select(`
          *,
          songs (
            id,
            title
          ),
          melody_midi_tracks (
            id,
            track_name,
            track_index
          )
        `)?.eq('user_id', user?.id)?.order('created_at', { ascending: false })?.limit(limit);

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error fetching sessions:', error);
      return { data: null, error: error?.message };
    }
  },

  // Get session by ID
  async getSessionById(sessionId) {
    try {
      const { data, error } = await supabase?.from('pitch_training_sessions')?.select(`
          *,
          songs (
            id,
            title
          ),
          melody_midi_tracks (
            id,
            track_name,
            track_index,
            midi_data
          )
        `)?.eq('id', sessionId)?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error fetching session:', error);
      return { data: null, error: error?.message };
    }
  },

  // Get session statistics
  async getSessionStats() {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('User not authenticated');

      const { data, error } = await supabase?.from('pitch_training_sessions')?.select('pitch_accuracy_percent, letter_grade, longest_streak, created_at, training_mode')?.eq('user_id', user?.id)?.eq('session_status', 'completed')?.order('created_at', { ascending: false });

      if (error) throw error;

      // Calculate statistics
      const stats = {
        totalSessions: data?.length,
        averageAccuracy: data?.reduce((sum, s) => sum + (s?.pitch_accuracy_percent || 0), 0) / data?.length || 0,
        bestAccuracy: Math.max(...data?.map(s => s?.pitch_accuracy_percent || 0), 0),
        longestStreak: Math.max(...data?.map(s => s?.longest_streak || 0), 0),
        gradeDistribution: data?.reduce((acc, s) => {
          const grade = s?.letter_grade || 'F';
          acc[grade] = (acc?.[grade] || 0) + 1;
          return acc;
        }, {}),
        modeDistribution: data?.reduce((acc, s) => {
          const mode = s?.training_mode || 'unknown';
          acc[mode] = (acc?.[mode] || 0) + 1;
          return acc;
        }, {}),
        recentImprovement: this.calculateImprovement(data)
      };

      return { data: stats, error: null };
    } catch (error) {
      console.error('Error fetching stats:', error);
      return { data: null, error: error?.message };
    }
  },

  // Helper: Calculate improvement trend
  calculateImprovement(sessions) {
    if (sessions?.length < 2) return 0;
    
    const recent = sessions?.slice(0, 5);
    const older = sessions?.slice(5, 10);
    
    if (older?.length === 0) return 0;
    
    const recentAvg = recent?.reduce((sum, s) => sum + (s?.pitch_accuracy_percent || 0), 0) / recent?.length;
    const olderAvg = older?.reduce((sum, s) => sum + (s?.pitch_accuracy_percent || 0), 0) / older?.length;
    
    return recentAvg - olderAvg;
  }
};